import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowRight, Check } from "lucide-react";
import { Reveal } from "@/components/motion";
import { IMAGES } from "@/data/site";

const POINTS = [
  "Bespoke resume, LinkedIn, and personal brand — one coherent system, not three separate deliverables",
  "Interview coaching grounded in how real hiring managers actually screen",
  "A complete Career Transition Programme for professionals making a deliberate change",
  "Honest guidance, not just validation — we tell you what will actually move the needle",
  // "Honest guidance, not just validation — we tell you what will actually move the needle",
  "Recruitment, RPO, talent acquisition, business development, hiring strategy & workforce planning, and HR & payroll for growing organisations",
];

export default function AboutPreview() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [40, -40]);

  return (
    <section data-testid="about-preview" className="bg-white py-8">
      <div className="nc-container grid gap-14 lg:grid-cols-2 lg:items-center">
        {/* image */}
        <div ref={ref} className="relative order-2 lg:order-1 flex justify-center">
          <motion.img
            style={{ y }}
            src={IMAGES.heroOffice}
            alt="North Cadence founding team"
            className="h-auto w-full max-w-sm sm:max-w-md lg:max-w-none rounded-2xl object-cover"
            loading="lazy"
          />
        </div>

        {/* copy */}
        <div className="order-1 lg:order-2">
          <SectionEyebrow>Who We Are</SectionEyebrow>
          <Reveal delay={0.05}>
            <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight leading-[1.12] text-[#1B1B1B]">
              A career and talent partner<br /> built for the <span className="text-[#142A5E]">people</span> who <span className="text-[#2FA86A]">mean it.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-6 text-lg leading-relaxed text-[#1B1B1B]/65">
              Most career services give you a template and call it coaching. North
              Cadence builds a bespoke strategy — for your goals, your industry,
              your timeline — and stays with you from first session to offer
              accepted.
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <ul className="mt-8 space-y-4">
              {POINTS.map((p) => (
                <li key={p} className="flex items-start gap-3">
                  <span className="mt-0.5 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#2FA86A]/15 text-[#2FA86A]">
                    <Check className="h-4 w-4" />
                  </span>
                  <span className="text-[#1B1B1B]/75">{p}</span>
                </li>
              ))}
            </ul>
          </Reveal>
          <Reveal delay={0.2}>
            <Link
              to="/about"
              data-testid="about-learn-more"
              className="group mt-9 inline-flex items-center gap-2 rounded-full bg-[#1B1B1B] px-6 py-3.5 font-semibold text-white transition-colors hover:bg-[#142A5E]"
            >
              Learn More
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

const SectionEyebrow = ({ children }) => (
  <Reveal>
    <span className="inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.2em] text-[#2FA86A]">
      <span className="h-px w-8 bg-[#2FA86A]" />
      {children}
    </span>
  </Reveal>
);
